import { useState, useMemo, useCallback } from "react"
import { Container, Row, Col, Form } from "react-bootstrap"
import { Search } from "react-bootstrap-icons"
import Select from "react-select"
import { getTimetableData } from "../../data/timetable.js"
import FilterSidebar from "../../components/class_timetable/FilterSidebar"
import WeekTabs from "../../components/class_timetable/weeksTab"
import SessionSection from "../../components/class_timetable/sessionCard"
import "./timetable.css"

const formatOptionLabel = ({ name, location, label }) => {
  if (!name) return <span>{label}</span>
  return (
    <span>
      <strong style={{ textTransform: "uppercase" }}>{name}</strong>
      {" — "}
      <span>{location}</span>
    </span>
  )
}

const venueSelectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: "6px",
    borderColor: state.isFocused ? "#f5b400" : "#ced4da",
    minHeight: "45px",
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(245, 180, 0, 0.25)" : "none",
    "&:hover": {
      borderColor: "#f5b400",
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#0d6efd"
      : state.isFocused
      ? "#e9f0ff"
      : "white",
    color: state.isSelected ? "white" : "inherit",
    cursor: "pointer",
  }),
  indicatorSeparator: () => ({ display: "none" }),
}

export default function Timetable() {
  const data = getTimetableData()

  // ── Week array (today → +6 days) ────────────────────────────────────────
  const getDayId = (jsDay) => (jsDay === 0 ? 7 : jsDay)

  const week = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(today)
      day.setDate(today.getDate() + i)
      const jsDay = day.getDay()
      const year  = day.getFullYear()
      const month = String(day.getMonth() + 1).padStart(2, "0")
      const date  = String(day.getDate()).padStart(2, "0")
      return {
        label  : day.toLocaleDateString("en-US", { weekday: "short" }),
        day    : day.getDate(),
        month  : day.getMonth() + 1,
        full   : `${year}-${month}-${date}`,
        isToday: i === 0,
        dayId  : getDayId(jsDay),
      }
    })
  }, [])

  const getTodayDate = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const year  = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, "0")
    const date  = String(today.getDate()).padStart(2, "0")
    return `${year}-${month}-${date}`
  }

  // ── State ────────────────────────────────────────────────────────────────
  const [selectedDate,  setSelectedDate]  = useState(getTodayDate())
  const [selectedClubs, setSelectedClubs] = useState([])
  const [searchQuery,   setSearchQuery]   = useState("")
  const [selectedVenue, setSelectedVenue] = useState("")

  // ── Venue options for React Select ──────────────────────────────────────
  const venueOptions = useMemo(() => [
    { value: "", label: "All Venues", name: "", location: "" },
    ...data.venues.map((venue) => ({
      value   : venue.id,
      label   : `${venue.name} — ${venue.location}`,
      name    : venue.name,
      location: venue.location,
    })),
  ], [data.venues])

  const selectedVenueOption = useMemo(
    () => venueOptions.find((o) => String(o.value) === String(selectedVenue)) ?? venueOptions[0],
    [venueOptions, selectedVenue]
  )

  // ── Helpers ──────────────────────────────────────────────────────────────
  const getDayNameFromDate = (dateString) => {
    const [year, month, day] = dateString.split("-").map(Number)
    const d = new Date(year, month - 1, day)
    const names = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"]
    return names[d.getDay()]
  }

  // ── Filtered sessions ────────────────────────────────────────────────────
  const getFilteredSessions = useCallback((sessionType) => {
    const dayName = getDayNameFromDate(selectedDate)
    const dayData = data.clubs[dayName]
    if (!dayData || !Array.isArray(dayData[sessionType])) return []

    return dayData[sessionType].filter((club) => {
      // 1. Venue filter
      if (selectedVenue !== "" && String(club.venueId) !== String(selectedVenue)) {
        return false
      }

      // 2. Class-type filter (sidebar checkboxes)
      if (selectedClubs.length > 0) {
        const matchesType = selectedClubs.some((filterLabel) => {
          const mappedName = data.classTypeMap[filterLabel] ?? filterLabel
          return club.name === mappedName
        })
        if (!matchesType) return false
      }

      // 3. Search
      if (searchQuery !== "" && !club.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false
      }

      return true
    })
  }, [selectedDate, selectedClubs, searchQuery, selectedVenue, data])

  const morningSessions   = useMemo(() => getFilteredSessions("morning"),   [getFilteredSessions])
  const afternoonSessions = useMemo(() => getFilteredSessions("afternoon"), [getFilteredSessions])
  const eveningSessions   = useMemo(() => getFilteredSessions("evening"),   [getFilteredSessions])

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="m-0 p-0">
      <div className="public-holiday-banner">
        Timetable may differ on public holidays.
      </div>

      <Container fluid className="timetable-container col-12">

        <div className="timetable-header">
          <h3 className="timetable-title">Class Timetable</h3>
        </div>

        <div className="filter-search-container col-md-4 col-lg-3">
          <Search className="filter-search-icon" />
          <Form.Control
            type="text"
            placeholder="Search"
            className="filter-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <Row className="timetable-content-row">

          {/* ── Sidebar ── */}
          <Col lg={3} md={4} className="filter-col">
            <FilterSidebar
              filters={data.filters}
              selected={selectedClubs}
              setSelected={setSelectedClubs}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </Col>

          {/* ── Main panel ── */}
          <Col lg={9} md={8} className="main-col">
            <div className="main-card">

              <div className="main-card-header">
                <h6 className="select-club-title">Select a Club</h6>
                <div className="venue-selector col-lg-6 col-md-8 col-12">
                  <label className="venue-label">Preferred Venue</label>
                  <Select
                    options={venueOptions}
                    value={selectedVenueOption}
                    onChange={(opt) => setSelectedVenue(opt?.value ?? "")}
                    formatOptionLabel={formatOptionLabel}
                    styles={venueSelectStyles}
                    isSearchable={false}
                    className="venue-dropdown"
                  />
                </div>
              </div>

              <WeekTabs
                week={week}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              />

              <SessionSection title="Morning"   sessions={morningSessions}   />
              <SessionSection title="Afternoon" sessions={afternoonSessions} />
              <SessionSection title="Evening"   sessions={eveningSessions}   />

            </div>
          </Col>

        </Row>
      </Container>
    </div>
  )
}